import React, { useEffect, useMemo, useState } from "react";
import { get, put } from "../../../../services/api";
import { useUserRole } from "../../../../context/UserRoleContext";

const NotificationSettings = () => {
  const { userData } = useUserRole();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const notificationTypes = useMemo(
    () => [
      {
        key: "show_booked",
        title: "Show Booked",
        description: "Get notified when a venue books you for a show",
      },
      {
        key: "show_cancelled",
        title: "Show Cancelled",
        description: "Get notified when a show is cancelled",
      },
      {
        key: "show_updated",
        title: "Show Updated",
        description: "Get notified when show details are updated",
      },
      {
        key: "new_message",
        title: "New Messages",
        description: "Get notified when you receive new messages",
      },
      {
        key: "invite_received",
        title: "Invite Received",
        description: "Get notified when you receive venue invites",
      },
    ],
    [],
  );

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const data = await get("/notifications/preferences");
        setPreferences(data.preferences || {});
      } catch (error) {
        setStatus({
          type: "error",
          message: "Failed to load notification settings.",
        });
      } finally {
        setLoading(false);
      }
    };
    loadPreferences();
  }, []);

  const handlePreferenceChange = (key, channel) => {
    const fieldKey = `${key}_${channel}`;
    setPreferences((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
    setStatus({ type: "", message: "" });
  };

  const handleSavePreferences = async () => {
    if (!preferences) return;
    setSaving(true);
    setStatus({ type: "", message: "" });
    try {
      const payload = {};
      notificationTypes.forEach(({ key }) => {
        const emailKey = `${key}_email`;
        const pushKey = `${key}_push`;
        payload[emailKey] = Boolean(preferences[emailKey]);
        payload[pushKey] = Boolean(preferences[pushKey]);
      });
      await put("/notifications/preferences", payload);
      setStatus({
        type: "success",
        message: "Notification settings saved.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: "Could not save notification settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-content">
      <div className="settings-section">
        <div className="section-header">
          <h2 className="section-title">Notification Settings</h2>
          <p className="section-subtitle">
            Choose how you want to be notified about activities
          </p>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <h3>Event Delivery Preferences</h3>
          </div>

          <div className="preferences-list">
            {loading && <p>Loading settings...</p>}
            {!loading &&
              notificationTypes.map((item) => (
                <div className="preference-item" key={item.key}>
                  <div className="preference-info">
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </div>
                  <div
                    style={{ display: "flex", gap: "20px", alignItems: "center" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <small>Email</small>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={Boolean(preferences?.[`${item.key}_email`])}
                          onChange={() =>
                            handlePreferenceChange(item.key, "email")
                          }
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <small>Push</small>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={Boolean(preferences?.[`${item.key}_push`])}
                          onChange={() => handlePreferenceChange(item.key, "push")}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className="settings-card">
          <div className="card-header">
            <h3>Delivery Notes</h3>
          </div>

          <div className="preferences-list">
            <div className="preference-item">
              <div className="preference-info">
                <h4>Email Address</h4>
                <p>
                  Notification emails are sent to: <strong>{userData?.email || "—"}</strong>
                </p>
              </div>
            </div>

            <div className="preference-item">
              <div className="preference-info">
                <h4>SMS Notifications</h4>
                <p>Coming soon. SMS is visible now but disabled.</p>
              </div>
              <label className="toggle-switch">
                <input type="checkbox" checked={false} disabled />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="form-actions">
          {status.message && (
            <p
              style={{
                marginBottom: "10px",
                color: status.type === "error" ? "#cc2a2a" : "#2d7f3f",
              }}
            >
              {status.message}
            </p>
          )}
          <button
            className="save-btn"
            onClick={handleSavePreferences}
            disabled={saving || loading}
          >
            {saving ? "Saving..." : "Save Preferences"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;

